"use client"; // Indicate this is a client component

import { api } from "@convex/_generated/api"; // Use alias
import { useMutation, useQuery } from "convex/react";
import { Loader2, Lock } from "lucide-react";
import React, { Suspense, useCallback, useEffect, useState } from "react";
import { useLocation, useParams } from "wouter";
import { Modal } from "@/components/_shared/Modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// Define the possible return types from the query
type LinkLoadResult =
	| { originalUrl: string; _id: any }
	| { passwordRequired: true; _id: any }
	| { incorrectPassword: true; _id: any }
	| null;

// Password Prompt Modal Component
function PasswordPromptModal({
	isOpen,
	onClose,
	onSubmit,
	error,
	loading,
}: {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (password: string) => void;
	error: string | null;
	loading: boolean;
}) {
	const [password, setPassword] = useState("");

	const handleSubmit = () => {
		onSubmit(password);
	};

	return (
		<Modal
			closeOnOutsideClick={false}
			isOpen={isOpen}
			onClose={onClose}
			title="Passwort erforderlich"
		>
			<div className="space-y-4 p-2">
				<div className="mb-2 flex items-center rounded-lg border border-amber-100 bg-amber-50 p-3 text-amber-800 dark:border-amber-800/30 dark:bg-amber-900/30 dark:text-amber-300">
					<Lock className="mr-2 h-5 w-5 shrink-0" />
					<p>
						Dieser Link ist passwortgeschützt. Bitte gib das Passwort ein, um
						weitergeleitet zu werden.
					</p>
				</div>

				{error && (
					<p className="text-red-500 text-sm dark:text-red-400">{error}</p>
				)}

				<Input
					className="w-full"
					disabled={loading}
					onChange={(e) => setPassword(e.target.value)}
					onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
					placeholder="Passwort eingeben"
					type="password"
					value={password}
				/>

				<div className="mt-4 flex justify-end space-x-2">
					<Button
						disabled={loading}
						onClick={onClose}
						type="button"
						variant="outline"
					>
						Abbrechen
					</Button>
					<Button
						disabled={loading || !password}
						onClick={handleSubmit}
						type="button"
					>
						{loading ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" /> Prüfen...
							</>
						) : (
							"Weiterleiten"
						)}
					</Button>
				</div>
			</div>
		</Modal>
	);
}

// Main Redirect Content Component
function RedirectContent({ shortCode }: { shortCode: string }) {
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [showPasswordModal, setShowPasswordModal] = useState(false);
	const [passwordError, setPasswordError] = useState<string | null>(null);
	const [isCheckingPassword, setIsCheckingPassword] = useState(false);
	const [errorType, setErrorType] = useState<"expired" | "notfound">(
		"notfound",
	);
	const [, navigate] = useLocation();
	const [hasRedirected, setHasRedirected] = useState(false);
	const [submittedPassword, setSubmittedPassword] = useState<
		string | undefined
	>(undefined);

	// Fetch link data - initially without password
	const linkQuery = useQuery(
		api.linkShares.linkPerKuerzelLaden,
		{
			kuerzel: shortCode,
			passwort: submittedPassword, // Pass submitted password if available
		},
		{ enabled: !hasRedirected }, // Only run query if not redirected yet
	);

	const incrementViews = useMutation(api.linkShares.aufrufZaehlen);

	// Effect to handle query result
	useEffect(() => {
		if (hasRedirected || !shortCode || linkQuery === undefined) return;

		const result = linkQuery as LinkLoadResult;

		if (result === null) {
			setErrorType(shortCode.length > 0 ? "expired" : "notfound");
			setShowErrorModal(true);
		} else if ("passwordRequired" in result && result.passwordRequired) {
			setShowPasswordModal(true);
		} else if ("incorrectPassword" in result && result.incorrectPassword) {
			setPasswordError("Falsches Passwort. Bitte versuche es erneut.");
			setIsCheckingPassword(false);
			setShowPasswordModal(true);
			setSubmittedPassword(undefined);
		} else if ("originalUrl" in result) {
			setHasRedirected(true);
			setShowPasswordModal(false);
			const destinationUrl = result.originalUrl;
			incrementViews({ kuerzel: shortCode }).catch((error) => {
				console.error("Fehler beim Zählen des Aufrufs:", error);
			});
			window.location.replace(destinationUrl);
		}
	}, [linkQuery, shortCode, incrementViews, hasRedirected]);

	// Handle password submission from modal
	const handlePasswordSubmit = useCallback((password: string) => {
		setPasswordError(null);
		setIsCheckingPassword(true);
		setSubmittedPassword(password);
	}, []);

	// Handle closing modals -> navigate away
	const handleModalClose = () => {
		setShowErrorModal(false);
		setShowPasswordModal(false);
		navigate("/link");
	};

	const isLoading =
		linkQuery === undefined &&
		!hasRedirected &&
		!showErrorModal &&
		!showPasswordModal;

	return (
		<>
			{/* Error Modal */}
			<Modal
				closeOnOutsideClick={false}
				isOpen={showErrorModal}
				onClose={handleModalClose}
				title={
					errorType === "expired" ? "Link abgelaufen" : "Link nicht gefunden"
				}
			>
				<div className="space-y-4">
					<p className="text-gray-600 dark:text-gray-300">
						{errorType === "expired"
							? "Der aufgerufene Link ist abgelaufen und nicht mehr gültig."
							: "Der angeforderte Link existiert nicht oder wurde falsch eingegeben."}
					</p>
					<div className="mt-4 flex justify-end">
						<Button
							className="bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
							onClick={handleModalClose}
							type="button"
						>
							Zum Link-Kürzer
						</Button>
					</div>
				</div>
			</Modal>

			{/* Password Prompt Modal */}
			<PasswordPromptModal
				error={passwordError}
				isOpen={showPasswordModal}
				loading={isCheckingPassword}
				onClose={handleModalClose}
				onSubmit={handlePasswordSubmit}
			/>

			{/* Loading Indicator */}
			{isLoading && (
				<div className="rounded-xl border border-white/50 bg-white/30 p-8 text-center shadow-lg backdrop-blur-sm dark:border-gray-700/50 dark:bg-gray-800/30">
					<div className="mb-4 inline-block h-16 w-16 animate-spin rounded-full border-4 border-blue-200 border-t-blue-500 dark:border-gray-600 dark:border-t-blue-400" />
					<p className="font-medium text-gray-700 text-xl dark:text-gray-200">
						Leite weiter...
					</p>
				</div>
			)}
		</>
	);
}

// Main Redirect Component
export default function LinkRedirectPage() {
	const params = useParams<{ short?: string }>();
	const shortCode = params?.short || "";

	useEffect(() => {
		document.title = "Weiterleitung...";
	}, []);

	// Use a minimal layout for faster redirection appearance
	return (
		<div className="flex min-h-screen items-center justify-center bg-linear-to-br from-blue-50 via-white to-blue-100 dark:from-gray-900 dark:via-gray-950 dark:to-gray-900">
			<Suspense
				fallback={
					<div className="text-center">
						<div className="mb-4 inline-block h-16 w-16 animate-spin rounded-full border-4 border-blue-200 border-t-blue-500 dark:border-gray-600 dark:border-t-blue-400" />
					</div>
				}
			>
				<RedirectContent shortCode={shortCode} />
			</Suspense>
		</div>
	);
}
